import { supabaseFetch } from "./supabaseClient";

/**
 * Transform a single collection result to V1 project shape.
 *
 * id stays the Firestore id (V1 routes key on it) and is null for V2-native
 * projects. v2Id is the V2 collection id, always present in the RPC payload:
 * it is the deep-link target for rows the V1 list can no longer open in the
 * builder (movedToV2, or no Firestore doc at all).
 */
function transformCollectionToProject(row) {
  return {
    id: row.firebase_project_id,
    v2Id: row.id ?? null,
    movedToV2: row.v2_authoritative === true,
    name: row.name,
    distributor: row.distributor_name || "",
    accountNumber: row.distributor_account_number || "",
    rep: row.rep_name || "",
    logoURL: row.logo_url || "",
    createdAt: row.created_at ? new Date(row.created_at) : null,
    modifiedAt: row.updated_at ? new Date(row.updated_at) : null,
  };
}

/**
 * Search collections for a V1 user. Returns results, facets, and total count.
 *
 * @param {string} firebaseUid - Firebase Auth UID of the current user
 * @param {string} query - Search term for collection name
 * @param {Object} filters - { distributor?: string, rep?: string }
 * @param {string} sortBy - Sort field:order (e.g., "modifiedAt:desc")
 * @param {number} page - Page number (1-based)
 * @param {number} perPage - Results per page
 */
export async function searchProjects(
  firebaseUid,
  query = "",
  filters = {},
  sortBy = "modifiedAt:desc",
  page = 1,
  perPage = 50
) {
  // Map V1 sort fields to Supabase column names
  const sortMap = {
    modifiedAt: "updated_at",
    createdAt: "created_at",
    name: "name",
  };
  const [sortField, sortOrder] = sortBy.split(":");
  const supabaseSortField = sortMap[sortField] || "updated_at";
  const supabaseSortOrder = sortOrder || "desc";

  const data = await supabaseFetch("/rpc/search_collections_for_v1", {
    method: "POST",
    body: JSON.stringify({
      p_firebase_uid: firebaseUid,
      p_query: query,
      p_distributor: filters.distributor || null,
      p_rep: filters.rep || null,
      p_sort_by: supabaseSortField,
      p_sort_order: supabaseSortOrder,
      p_limit: perPage,
      p_offset: (page - 1) * perPage,
    }),
  });

  return {
    projects: (data.results || []).map(transformCollectionToProject),
    facetCounts: [
      {
        field_name: "distributor",
        counts: (data.facets?.distributors || []).map((f) => ({
          value: f.value,
          count: f.count,
        })),
      },
      {
        field_name: "rep",
        counts: (data.facets?.reps || []).map((f) => ({
          value: f.value,
          count: f.count,
        })),
      },
    ],
    totalCount: data.total_count || 0,
  };
}
